/**
 * 用户管理 (Admin) — 列表、状态切换、角色修改、分组分配
 */

import { authFetch } from './api.js';
import { getStoredUser } from '../interaction/transport/http-client.js';
import {
  getCurrentView,
  getUserRole,
  getUserApprovalsCurrentPage,
  getUsersCurrentPage,
  setUserApprovalsCurrentPage,
  setUsersCurrentPage,
} from './state.js';
import { showToast, formatDate, renderPagination } from './utils.js';
import { refreshOpsNavBadge, registerViewLoader } from './navigation.js';
import { readScopedQuery, writeScopedQuery } from './url-state.js';
import { withTableLoading } from './view-state.js';
import { createUserEditModalController } from './user-edit-modal.js';

function getInitials(value) {
  const rawValue = String(value || '').trim();
  if (!rawValue) return 'NA';
  const normalized = rawValue.replace(/[^a-zA-Z0-9\u3400-\u9fff]/g, '');
  return (normalized.slice(0, 2) || 'NA').toUpperCase();
}

const USERS_QUERY_SCHEMA = {
  page: { type: 'int', defaultValue: 1 },
  search: { type: 'string', defaultValue: '' },
  role: { type: 'string', defaultValue: '' },
  status: { type: 'string', defaultValue: '' },
  approval: { type: 'string', defaultValue: '' },
};
const USER_APPROVALS_QUERY_SCHEMA = {
  page: { type: 'int', defaultValue: 1 },
  search: { type: 'string', defaultValue: '' },
  role: { type: 'string', defaultValue: '' },
  status: { type: 'string', defaultValue: '' },
  approval: { type: 'string', defaultValue: '' },
};
const USER_PAGE_SIZE = 20;
const USER_VIEW_CONFIG = {
  manage: {
    viewName: 'admin-users',
    queryPrefix: 'users',
    mode: 'manage',
    tbodyId: 'users-tbody',
    paginationId: 'users-pagination',
    colSpan: 7,
    emptyText: '暂无已通过成员',
    errorMessage: '加载成员管理列表失败',
    currentPageGetter: getUsersCurrentPage,
    currentPageSetter: setUsersCurrentPage,
    fields: {
      search: 'search-users',
      role: 'filter-role',
      status: 'filter-status',
      approval: null,
      filterButton: 'filter-users-btn',
    },
    querySchema: USERS_QUERY_SCHEMA,
  },
  review: {
    viewName: 'admin-user-approvals',
    queryPrefix: 'user_approvals',
    mode: 'review',
    tbodyId: 'user-approvals-container',
    paginationId: 'user-approvals-pagination',
    colSpan: 9,
    emptyText: '暂无成员审核记录',
    errorMessage: '加载成员审核列表失败',
    currentPageGetter: getUserApprovalsCurrentPage,
    currentPageSetter: setUserApprovalsCurrentPage,
    fields: {
      search: 'search-user-approvals',
      role: 'filter-approval-role',
      status: 'filter-approval-status',
      approval: 'filter-approval-status-review',
      filterButton: 'filter-user-approvals-btn',
    },
    querySchema: USER_APPROVALS_QUERY_SCHEMA,
  },
};

const userEditModal = createUserEditModalController({
  authFetch,
  showToast,
  reloadActiveUserView,
  createElement: createUserElement,
});

export function initUsers() {
  hydrateUserFiltersFromUrl(USER_VIEW_CONFIG.manage);
  hydrateUserFiltersFromUrl(USER_VIEW_CONFIG.review);
  registerViewLoader('admin-users', (page) => loadAdminUsers(page));
  registerViewLoader('admin-user-approvals', (page) => loadAdminUserApprovals(page));

  Object.values(USER_VIEW_CONFIG).forEach((config) => {
    const filterBtn = document.getElementById(config.fields.filterButton);
    if (filterBtn) filterBtn.addEventListener('click', () => loadUserDirectory(config, 1));
  });
  userEditModal.bindDomEvents();
  bindApprovalQuickFilters();
  document.getElementById(USER_VIEW_CONFIG.review.fields.approval)?.addEventListener('change', (event) => {
    syncApprovalQuickFilters(event.target.value || USER_APPROVALS_QUERY_SCHEMA.approval.defaultValue);
  });
}

function hydrateUserFiltersFromUrl(config) {
  const state = readScopedQuery(config.queryPrefix, config.querySchema);
  const searchInput = document.getElementById(config.fields.search);
  const roleSelect = document.getElementById(config.fields.role);
  const statusSelect = document.getElementById(config.fields.status);
  const approvalSelect = config.fields.approval ? document.getElementById(config.fields.approval) : null;
  if (searchInput && state.search) searchInput.value = state.search;
  if (roleSelect) roleSelect.value = state.role || '';
  if (statusSelect) statusSelect.value = state.status || '';
  if (approvalSelect) approvalSelect.value = state.approval || '';
  if (config.mode === 'review') syncApprovalQuickFilters(state.approval || USER_APPROVALS_QUERY_SCHEMA.approval.defaultValue);
}

export async function loadAdminUsers(page = null) {
  return loadUserDirectory(USER_VIEW_CONFIG.manage, page);
}

export async function loadAdminUserApprovals(page = null) {
  return loadUserDirectory(USER_VIEW_CONFIG.review, page);
}

function hasUserDirectoryFilters({ search, role, status, approval }) {
  return Boolean(search || role || status || approval);
}

function resetUserDirectoryFilters(config) {
  const searchInput = document.getElementById(config.fields.search);
  const roleSelect = document.getElementById(config.fields.role);
  const statusSelect = document.getElementById(config.fields.status);
  const approvalSelect = config.fields.approval ? document.getElementById(config.fields.approval) : null;

  if (searchInput) searchInput.value = '';
  if (roleSelect) roleSelect.value = '';
  if (statusSelect) statusSelect.value = '';
  if (approvalSelect) approvalSelect.value = '';
  if (config.mode === 'review') {
    syncApprovalQuickFilters(USER_APPROVALS_QUERY_SCHEMA.approval.defaultValue);
  }
  writeScopedQuery(config.queryPrefix, {
    page: 1,
    search: '',
    role: '',
    status: '',
    approval: '',
  });
  config.currentPageSetter(1);
}

async function loadUserDirectory(config, page = null) {
  const queryState = readScopedQuery(config.queryPrefix, config.querySchema);
  const targetPage = page == null ? queryState.page : page;
  const search = document.getElementById(config.fields.search)?.value?.trim() || '';
  const role = document.getElementById(config.fields.role)?.value || '';
  const status = document.getElementById(config.fields.status)?.value || '';
  const approval = config.fields.approval
    ? (document.getElementById(config.fields.approval)?.value || '')
    : '';
  const tbody = document.getElementById(config.tbodyId);
  if (config.mode === 'review') syncApprovalQuickFilters(approval || USER_APPROVALS_QUERY_SCHEMA.approval.defaultValue);
  writeScopedQuery(config.queryPrefix, { page: targetPage, search, role, status, approval });
  config.currentPageSetter(targetPage);

  const data = await withTableLoading({
    tbody,
    colSpan: config.colSpan,
    page: targetPage,
    errorMessage: config.errorMessage,
    onRetry: (p) => loadUserDirectory(config, p),
    onError: (error) => {
      console.error(`Error loading ${config.viewName}:`, error);
      showToast(config.errorMessage, 'error');
    },
    task: async () => {
      const params = new URLSearchParams({
        page: targetPage,
        size: USER_PAGE_SIZE,
        ...(config.mode && { mode: config.mode }),
        ...(search && { search }),
        ...(role && { role }),
        ...(status && { status }),
        ...(approval && { approval }),
      });
      const response = await authFetch(`/api/admin/users?${params}`);
      if (!response.ok) throw new Error('Failed to load users');
      return response.json();
    },
  });
  if (!data) return;

  if (
    config.mode === 'manage' &&
    page == null &&
    data.total === 0 &&
    targetPage === 1 &&
    !search &&
    !role &&
    !approval &&
    status === 'disabled'
  ) {
    resetUserDirectoryFilters(config);
    showToast('已自动清除遗留的“禁用”筛选，恢复为全部状态', 'info');
    return loadUserDirectory(config, 1);
  }

  try {
    renderUsersTable(config, data.users, data.total, targetPage, USER_PAGE_SIZE, {
      search,
      role,
      status,
      approval,
    });
  } catch (error) {
    console.error('Failed to render user directory', error);
    const container = document.getElementById(config.tbodyId);
    renderUserDirectoryError(container, config);
    showToast('成员列表渲染失败，请刷新后重试', 'error');
  }
}

function createUserElement(tagName, className = '', text = undefined) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== undefined) element.textContent = String(text);
  return element;
}

function createUserEmptyState(icon, text) {
  const state = createUserElement('div', 'empty-state');
  state.appendChild(createUserElement('div', 'empty-icon', icon));
  state.appendChild(createUserElement('p', '', text));
  return state;
}

function createUserTableState(content, colSpan) {
  const row = document.createElement('tr');
  const cell = document.createElement('td');
  cell.colSpan = colSpan;
  cell.appendChild(content);
  row.appendChild(cell);
  return row;
}

function appendUserActionButton(parent, { className = 'btn btn-sm', action, id, arg1, text }) {
  const button = createUserElement('button', className, text);
  button.type = 'button';
  button.dataset.action = action;
  button.dataset.id = String(id ?? '');
  if (arg1 !== undefined) button.dataset.arg1 = String(arg1 ?? '');
  parent.appendChild(button);
}

function renderUserDirectoryError(container, config) {
  if (!container) return;
  container.className = '';
  if (config.mode === 'manage') {
    const row = document.createElement('tr');
    const cell = createUserElement('td', 'error', '渲染成员列表失败，请刷新后重试');
    cell.colSpan = config.colSpan;
    row.appendChild(cell);
    container.replaceChildren(row);
    return;
  }
  container.replaceChildren(createUserEmptyState('⚠️', '渲染成员列表失败，请刷新后重试'));
}

function createManagedUserRow(user, { canAdminManage, currentUserId }) {
  const row = document.createElement('tr');
  const initials = getInitials(user.name || user.email);
  const role = user.role === 'admin' || user.role === 'leader' ? user.role : 'member';
  const roleLabel = role === 'admin' ? '管理员' : role === 'leader' ? '组长' : '成员';
  const status = user.status === 'active' ? 'active' : 'disabled';
  const statusLabel = status === 'active' ? '活跃' : '已禁用';
  const groupLabel = user.groupName || (user.groupId ? `组 #${user.groupId}` : '-');
  const lastLogin = user.lastLogin ? formatDate(user.lastLogin) : '从未登录';

  const userCell = document.createElement('td');
  const userWrap = createUserElement('div', 'users-td-user');
  userWrap.appendChild(createUserElement('div', 'users-td-avatar', initials));
  userWrap.appendChild(createUserElement('span', 'users-td-name', user.name || 'N/A'));
  userCell.appendChild(userWrap);
  row.appendChild(userCell);

  row.appendChild(createUserElement('td', 'mono users-td-secondary', user.email || ''));
  row.appendChild(createUserElement('td', '', groupLabel));

  const roleCell = document.createElement('td');
  roleCell.appendChild(createUserElement('span', `badge badge-${role}`, roleLabel));
  row.appendChild(roleCell);

  const statusCell = document.createElement('td');
  statusCell.appendChild(createUserElement('span', `badge badge-${status}`, statusLabel));
  row.appendChild(statusCell);

  row.appendChild(createUserElement('td', 'mono users-td-secondary', lastLogin));

  const actionCell = document.createElement('td');
  const actions = createUserElement('div', 'user-action-cell');
  if (String(user.id) === String(currentUserId)) {
    actions.appendChild(createUserElement('span', 'user-action-empty-state', '当前用户'));
  } else {
    appendUserActionButton(actions, { action: 'edit-user', id: user.id, text: '编辑' });
    appendUserActionButton(actions, {
      action: 'bind-erp-user',
      id: user.id,
      arg1: user.name || user.email,
      text: user.erpUserId ? '重绑ERP' : '绑定ERP',
    });
    if (canAdminManage) {
      appendUserActionButton(actions, {
        action: 'reset-password-user',
        id: user.id,
        arg1: user.name || user.email,
        text: '重置密码',
      });
      appendUserActionButton(actions, {
        className: `btn ${status === 'active' ? 'btn-danger' : 'btn-success'} btn-sm`,
        action: 'toggle-user',
        id: user.id,
        arg1: status,
        text: status === 'active' ? '禁用' : '启用',
      });
      appendUserActionButton(actions, {
        className: 'btn btn-danger btn-sm',
        action: 'delete-user',
        id: user.id,
        arg1: user.name || user.email,
        text: '删除',
      });
    }
  }
  if (!actions.childElementCount) {
    actions.appendChild(createUserElement('span', 'user-action-empty-state', '无可用操作'));
  }
  actionCell.appendChild(actions);
  row.appendChild(actionCell);
  return row;
}

function createApprovalCard(user, canReview) {
  const rawName = user.name || user.email || 'NA';
  const initials = (rawName.replace(/[^a-zA-Z0-9\u3400-\u9fff]/g, '').slice(0, 2) || 'NA').toUpperCase();
  const appliedAt = user.createdAt
    ? formatDate(user.createdAt)
    : (user.lastLogin ? formatDate(user.lastLogin) : '—');
  const groupLabel = user.groupName
    ? `${user.groupName} · #${user.groupId || ''}`
    : (user.groupId ? `组 #${user.groupId}` : '-');

  const card = createUserElement('div', 'approval-card');
  const left = createUserElement('div', 'approval-card-left');
  left.appendChild(createUserElement('div', 'approval-avatar', initials));
  const info = createUserElement('div', 'approval-info');
  info.appendChild(createUserElement('div', 'approval-name', user.name || 'N/A'));
  info.appendChild(createUserElement('div', 'approval-email', user.email || '-'));
  const meta = createUserElement('div', 'approval-meta');
  for (const [label, value] of [['申请分组:', groupLabel], ['申请时间:', appliedAt]]) {
    const item = createUserElement('div', 'approval-meta-item');
    item.appendChild(createUserElement('span', 'approval-meta-label', label));
    item.appendChild(createUserElement('span', '', value));
    meta.appendChild(item);
  }
  info.appendChild(meta);
  left.appendChild(info);
  card.appendChild(left);

  const actions = createUserElement('div', 'approval-actions');
  if (user.approvalStatus === 'pending' && canReview) {
    appendUserActionButton(actions, { className: 'btn btn-success', action: 'approve-user', id: user.id, text: '通过' });
    appendUserActionButton(actions, { className: 'btn btn-danger', action: 'reject-user', id: user.id, text: '拒绝' });
  } else if (user.approvalStatus === 'approved') {
    actions.appendChild(createUserElement('span', 'approval-action-status approval-action-status--approved', '已通过'));
  } else if (user.approvalStatus === 'rejected') {
    actions.appendChild(createUserElement('span', 'approval-action-status approval-action-status--rejected', '已拒绝'));
  } else {
    actions.appendChild(createUserElement('span', 'approval-action-status', '无可用操作'));
  }
  card.appendChild(actions);
  return card;
}

function renderUsersTable(config, users, total, page, size, filters = {}) {
  const container = document.getElementById(config.tbodyId);
  const currentRole = getCurrentUserRole();
  const canReview = currentRole === 'admin' || currentRole === 'leader';
  const canAdminManage = currentRole === 'admin';

  if (!users || users.length === 0) {
    const hasFilters = hasUserDirectoryFilters(filters);
    const emptyText = hasFilters ? '当前筛选条件下没有成员数据' : config.emptyText;
    const emptyState = createUserEmptyState('👥', emptyText);
    if (hasFilters) {
      const resetButton = createUserElement('button', 'btn btn-secondary btn-sm users-reset-filters-btn', '清空筛选');
      resetButton.type = 'button';
      resetButton.id = 'users-reset-filters-btn';
      resetButton.addEventListener('click', () => {
        resetUserDirectoryFilters(config);
        loadUserDirectory(config, 1);
      });
      emptyState.appendChild(resetButton);
    }
    if (config.mode === 'manage') {
      container.replaceChildren(createUserTableState(emptyState, config.colSpan));
    } else {
      container.replaceChildren(emptyState);
    }
    return;
  }

  // 成员审核页使用卡片布局
  if (config.mode === 'review') {
    const pendingUsers = users.filter((user) => user.approvalStatus === 'pending');
    const processedUsers = users.filter((user) => user.approvalStatus !== 'pending');
    container.className = '';
    const pendingSection = createUserElement('section', 'approval-section');
    const pendingTitle = createUserElement('div', 'approval-section-title');
    pendingTitle.appendChild(createUserElement('span', '', '待审核'));
    pendingTitle.appendChild(createUserElement('span', 'approval-section-count', pendingUsers.length));
    const pendingGrid = createUserElement('div', 'approval-cards-grid');
    pendingGrid.replaceChildren(...(pendingUsers.length
      ? pendingUsers.map((user) => createApprovalCard(user, canReview))
      : [createUserEmptyState('📝', '当前没有待审核申请')]));
    pendingSection.append(pendingTitle, pendingGrid);

    const processedSection = createUserElement('section', 'approval-section');
    const processedTitle = createUserElement('div', 'approval-section-title');
    processedTitle.appendChild(createUserElement('span', '', '已处理'));
    processedTitle.appendChild(createUserElement('span', 'approval-section-count', processedUsers.length));
    const toggle = createUserElement('button', 'approval-processed-toggle', '收起');
    toggle.type = 'button';
    toggle.id = 'approval-processed-toggle';
    toggle.setAttribute('aria-expanded', 'true');
    toggle.disabled = !processedUsers.length;
    processedTitle.appendChild(toggle);
    const panel = createUserElement('div', 'approval-processed-panel');
    panel.id = 'approval-processed-panel';
    const processedGrid = createUserElement('div', 'approval-cards-grid');
    processedGrid.replaceChildren(...(processedUsers.length
      ? processedUsers.map((user) => createApprovalCard(user, canReview))
      : [createUserEmptyState('📁', '当前没有已处理记录')]));
    panel.appendChild(processedGrid);
    processedSection.append(processedTitle, panel);
    container.replaceChildren(pendingSection, processedSection);

    if (processedUsers.length) {
      toggle.addEventListener('click', () => {
        const expanded = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        toggle.textContent = expanded ? '展开' : '收起';
        panel.hidden = expanded;
      });
    }
  } else {
    // 成员管理页表格布局
    container.className = '';
    container.replaceChildren(...users.map((user) => createManagedUserRow(user, {
      canAdminManage,
      currentUserId: getCurrentUserId(),
    })));
  }

  renderPagination(config.paginationId, total, page, size, (p) => loadUserDirectory(config, p));
}

function getCurrentUserId() {
  return getStoredUser()?.id || null;
}

function getCurrentUserRole() {
  // state.js _userRole is the authoritative source, kept fresh by checkUserRole()
  // via /api/auth/me before any view renders. sessionStorage may be stale
  // (role changed post-login, or empty when this page opens in a fresh tab).
  return getUserRole() || getStoredUser()?.role || 'member';
}

function bindApprovalQuickFilters() {
  document.querySelectorAll('#user-approval-quick-filter [data-approval-quick]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const approval = btn.dataset.approvalQuick || USER_APPROVALS_QUERY_SCHEMA.approval.defaultValue;
      const select = document.getElementById(USER_VIEW_CONFIG.review.fields.approval);
      if (select) select.value = approval;
      syncApprovalQuickFilters(approval);
      loadUserDirectory(USER_VIEW_CONFIG.review, 1);
    });
  });
}

function syncApprovalQuickFilters(activeApproval) {
  document.querySelectorAll('#user-approval-quick-filter [data-approval-quick]').forEach((btn) => {
    btn.classList.toggle('active', btn.dataset.approvalQuick === activeApproval);
  });
}

function getActiveUserViewConfig() {
  return getCurrentView() === USER_VIEW_CONFIG.review.viewName
    ? USER_VIEW_CONFIG.review
    : USER_VIEW_CONFIG.manage;
}

async function reloadActiveUserView() {
  const config = getActiveUserViewConfig();
  const page = config.currentPageGetter();
  await loadUserDirectory(config, page);
}

export async function toggleUserStatus(userId, currentStatus) {
  const newStatus = currentStatus === 'active' ? '禁用' : '启用';
  if (!confirm(`确定要${newStatus}该用户吗？`)) return;

  try {
    const response = await authFetch(`/api/admin/users/${userId}/toggle`, { method: 'POST' });
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || '操作失败');
    }

    showToast(`用户已${newStatus}`, 'success');
    await reloadActiveUserView();
  } catch (error) {
    console.error('Error toggling user status:', error);
    showToast(error.message || '操作失败', 'error');
  }
}

export async function updateUserApproval(userId, approvalStatus) {
  const actionLabel = approvalStatus === 'approved' ? '通过' : '拒绝';
  if (!confirm(`确定要${actionLabel}该注册申请吗？`)) return;

  try {
    const response = await authFetch(`/api/admin/users/${userId}/approval`, {
      method: 'POST',
      body: JSON.stringify({ approvalStatus }),
    });
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || '审批失败');
    }

    showToast(`申请已${actionLabel}`, 'success');
    await reloadActiveUserView();
    await refreshOpsNavBadge(authFetch);
  } catch (error) {
    console.error('Error updating user approval:', error);
    showToast(error.message || '审批失败', 'error');
  }
}

export async function changeUserRole(userId, currentRole, userName) {
  await userEditModal.open({
    userId: Number(userId),
    userName,
    currentRole,
  });
}

export async function assignGroup(userId, userName) {
  await userEditModal.open({
    userId: Number(userId),
    userName,
    focusGroupOnly: true,
  });
}

export async function bindErpUser(userId, userName) {
  await userEditModal.open({
    userId: Number(userId),
    userName,
    focusErpOnly: true,
  });
}

export async function resetUserPassword(userId, userName) {
  const modal = document.getElementById('user-reset-password-modal');
  const passwordInput = document.getElementById('user-reset-password-input');
  const titleEl = document.getElementById('user-reset-password-title');
  const hintEl = document.getElementById('user-reset-password-hint');
  if (!modal || !passwordInput) {
    showToast('重置密码弹窗未找到', 'error');
    return;
  }
  titleEl.textContent = `重置密码：${userName || ''}`;
  passwordInput.value = '';
  if (hintEl) hintEl.textContent = '';
  modal.hidden = false;
  passwordInput.focus();

  const cleanup = () => {
    modal.hidden = true;
    submitBtn?.removeEventListener('click', handleSubmit);
    cancelBtn?.removeEventListener('click', handleCancel);
  };
  const submitBtn = document.getElementById('user-reset-password-submit');
  const cancelBtn = document.getElementById('user-reset-password-cancel');

  async function handleSubmit() {
    const newPassword = passwordInput.value;
    if (!newPassword || newPassword.length < 6) {
      if (hintEl) hintEl.textContent = '密码至少 6 个字符';
      return;
    }
    if (newPassword.length > 72) {
      if (hintEl) hintEl.textContent = '密码不能超过 72 个字符';
      return;
    }
    if (!/[a-zA-Z]/.test(newPassword) || !/[0-9]/.test(newPassword)) {
      if (hintEl) hintEl.textContent = '密码必须包含字母和数字';
      return;
    }
    submitBtn.disabled = true;
    submitBtn.textContent = '重置中...';
    try {
      const response = await authFetch(`/api/admin/users/${userId}/reset-password`, {
        method: 'POST',
        body: JSON.stringify({ newPassword }),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || '重置失败');
      }
      showToast(`用户「${userName || ''}」密码已重置`, 'success');
      cleanup();
    } catch (error) {
      if (hintEl) hintEl.textContent = error.message || '重置失败';
    } finally {
      submitBtn.disabled = false;
      submitBtn.textContent = '确认重置';
    }
  }

  function handleCancel() {
    cleanup();
  }

  submitBtn.addEventListener('click', handleSubmit);
  cancelBtn.addEventListener('click', handleCancel);
}

export async function deleteUser(userId, userName) {
  if (!confirm(`确定要删除用户「${userName || ''}」吗？\n\n删除后该用户将无法登录，数据保留在数据库中。`)) return;

  try {
    const response = await authFetch(`/api/admin/users/${userId}`, { method: 'DELETE' });
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || '删除失败');
    }
    showToast(`用户「${userName || ''}」已删除`, 'success');
    await reloadActiveUserView();
  } catch (error) {
    console.error('Error deleting user:', error);
    showToast(error.message || '删除失败', 'error');
  }
}
